// @flow
import { DEFAULT_STATUS_FOR_ARCHIVED_SEARCH, DEFAULT_STATUS_FOR_ONLINE_SEARCH } from '../../utils/queryParams';
import { getAuthToken, LOG_OUT_BIDDER } from './user';
import { getFollowSearchAnalytics, getUnfollowSearchAnalytics } from './analytics';
import { getSavedSearchId, submitIsSavedSearchCheck } from './saveSearchCheck';
import api from '../api/saveSearch';
import cloneDeep from 'lodash/cloneDeep';
import { fetchSavedSearchesIfNeeded } from './savedSearch';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';

/* Action Types */
export const SAVE_SEARCH_FAIL = 'la/ui/saveSearch/FAIL';
export const SAVE_SEARCH_REQUEST = 'la/ui/saveSearch/REQUEST';
export const SAVE_SEARCH_SUCCESS = 'la/ui/saveSearch/SUCCESS';

export const DELETE_SAVE_SEARCH_FAIL = 'la/ui/deleteSaveSearch/FAIL';
export const DELETE_SAVE_SEARCH_REQUEST = 'la/ui/deleteSaveSearch/REQUEST';
export const DELETE_SAVE_SEARCH_SUCCESS = 'la/ui/deleteSaveSearch/SUCCESS';

// reducer
const DEFAULT_STATE = {
    byParams: {},
    error: false,
    //TODO remove these and update places using them to use byParams
    submitted: false,
    success: false,
};

export type State = {
    byParams: {
        deleted: boolean,
        key: string,
        submitted: boolean,
        success: boolean,
    },
    deleted?: boolean,
    error: boolean,
    submitted: boolean,
    success: boolean,
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    const existing = cloneDeep(state.byParams);
    switch (action.type) {
        case SAVE_SEARCH_REQUEST:
        case DELETE_SAVE_SEARCH_REQUEST:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        deleted: false,
                        key: `${action.payload}`,
                        submitted: true,
                        success: false,
                    },
                },
                submitted: true,
                success: false,
            };
        case SAVE_SEARCH_SUCCESS:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        deleted: false,
                        key: `${action.payload}`,
                        submitted: false,
                        success: true,
                    },
                },
                submitted: false,
                success: true,
            };
        case LOG_OUT_BIDDER:
            return {
                ...state,
                byParams: {},
                submitted: false,
                success: true,
            };
        case DELETE_SAVE_SEARCH_SUCCESS:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        deleted: true,
                        key: `${action.payload}`,
                        submitted: false,
                        success: true,
                    },
                },
                submitted: false,
                success: true,
            };
        case SAVE_SEARCH_FAIL:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        deleted: false,
                        key: `${action.payload}`,
                        submitted: false,
                        success: false,
                    },
                },
                error: true,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
export const getSaveSearchState = (state: GlobalState) => state.saveSearch;

export const getSaveSearchParams = (queryParams: any, pathname: string = '') => {
    const newParams = cloneDeep(queryParams);

    if (newParams.status === DEFAULT_STATUS_FOR_ARCHIVED_SEARCH) {
        newParams.status = DEFAULT_STATUS_FOR_ONLINE_SEARCH;
    }

    delete newParams.pageSize;
    delete newParams.pagesize;
    delete newParams.page;
    delete newParams.sort;
    delete newParams.saveSearch;

    newParams.keyword = newParams.keyword ? newParams.keyword : '';

    if (pathname.includes('/c/') && !newParams.campaign_id) {
        let pathCategoryIds = pathname.match(/\d+/g);
        pathCategoryIds = pathCategoryIds ? pathCategoryIds.join() : '';

        if (!newParams.category_id) {
            newParams.category_id = pathCategoryIds;
        } else {
            newParams.category_id = `${pathCategoryIds},${newParams.category_id}`;
        }
    }

    return newParams;
};

export const createSaveSearchParams = (categoryId: string) => {
    return {
        category_id: categoryId,
    };
};

export const createSaveSearchKey = (categoryId: string) => {
    return createSaveSearchKeyBySearchParams(createSaveSearchParams(categoryId));
};

export const createSaveSearchKeyBySearchParams = (searchParams: any): string => {
    const sortedParams = {};
    Object.keys(searchParams)
        .sort()
        .forEach(key => {
            sortedParams[key] = searchParams[key];
        });
    let key = JSON.stringify(sortedParams);
    key = key.replace(/}|{|"/g, '');
    return key;
};

/* ACTION CREATORS */
export const submitSaveSearch = (ssParams: any, sendAlert: boolean) => async (dispatch: any, getState: any) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const analytics = getFollowSearchAnalytics();

        dispatch({
            payload: createSaveSearchKeyBySearchParams(ssParams),
            type: SAVE_SEARCH_REQUEST,
        });
        await api.postSaveSearch({ authToken, deployment, sendAlert, ssParams });
        dispatch({
            meta: analytics,
            payload: createSaveSearchKeyBySearchParams(ssParams),
            type: SAVE_SEARCH_SUCCESS,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
        dispatch(submitIsSavedSearchCheck(ssParams));
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: SAVE_SEARCH_FAIL,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
        dispatch(submitIsSavedSearchCheck(ssParams));
    }
};

export const deleteSavedSearch = (ssParams: any) => async (dispatch: any, getState: any) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const savedSearchId = getSavedSearchId(state);
        const analytics = getUnfollowSearchAnalytics();

        dispatch({
            payload: createSaveSearchKeyBySearchParams(ssParams),
            type: DELETE_SAVE_SEARCH_REQUEST,
        });

        await api.postDeleteSavedSearch({ authToken, deployment, savedSearchId });
        dispatch({
            meta: analytics,
            payload: createSaveSearchKeyBySearchParams(ssParams),
            type: DELETE_SAVE_SEARCH_SUCCESS,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
        dispatch(submitIsSavedSearchCheck(ssParams));
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: DELETE_SAVE_SEARCH_FAIL,
        });
        dispatch(fetchSavedSearchesIfNeeded(true));
        dispatch(submitIsSavedSearchCheck(ssParams));
    }
};
